import { Fragment, useMemo } from "react";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";

import { QueryMetaData } from "@/app/gyms/gyms";

import AvatarTable from "../atoms/AvatarTable";
import { ButtonSecondary } from "../atoms/ButtonSecondary";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import PaginationUI from "../atoms/PaginationUI";
import PersonInformationTable from "../atoms/PersonInformationTable";
import { TableLoader } from "../molecules/TableLoader";
import { Empty } from "./Empty";
import { DOMAIN } from "@/app/app.api";

type FilialProps = {
  id: number | string;
  logo: string;
  branch: string | null;
  email: string | null;
  web: string | null;
  phone: string | null;
  country: string | null;
  code_zip: string | null;
  state: string | null;
  population: string | null;
  active_users_count: number;
};

type statsProps = {
  name: string;
  stat: string;
};

export type TableListGymsProps = {
  filiales: FilialProps[];
  title: string;
  description: string;
  handlePageChange: (page: number) => void;
  handleEntriesChange: (entries: number) => void;
  entries: number;
  queryMetaData: QueryMetaData;
  isLoading: boolean;
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClickTop: () => void;
  stats: statsProps[];
  openKpi: boolean;
  OnClickKPI: () => void;
};

const TableListGyms: React.FC<TableListGymsProps> = ({
  title,
  description,
  filiales,
  isLoading,
  searchQuery,
  onSearchChange,
  onClickTop,
  stats,
  openKpi,
  OnClickKPI,
  handlePageChange,
  handleEntriesChange,
  entries,
  queryMetaData,
}) => {
  const navigate = useNavigate();

  // Filter the filiales based on the single search query across all fields
  const filteredFiliales = useMemo(() => {
    if (!searchQuery) return filiales; // Return all filiales if searchQuery is empty

    const query = searchQuery.toLowerCase();
    return filiales.filter((filial) =>
      [
        filial.branch || "",
        filial.email || "",
        filial.country || "",
        filial.code_zip || "",
        `${filial.state || ""} ${filial.population || ""}`,
        filial.web || "",
      ].some((field) => field.toLowerCase().includes(query))
    );
  }, [filiales, searchQuery]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {title}
          </h1>
          <p className="mt-2 text-sm text-gray-700">{description}</p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex sm:items-center sm:space-x-2"></div>
      </div>

      {/* Divider debajo del texto */}
      <div className="border-b border-gray-300 my-3"></div>

      {openKpi && (
        <>
          <div>
            <dl
              className={`mt-5 mb-3 grid ${
                stats.length === 1
                  ? "grid-cols-1"
                  : "grid-cols-1 lg:grid-cols-4"
              } gap-4`}
            >
              {stats.map((item) => (
                <div
                  key={item.name}
                  className="border border-gray-200 h-30 rounded-sm"
                >
                  <div className="px-4 py-5 sm:p-6">
                    <dt className="text-base font-normal text-gray-900">
                      {item.name}
                    </dt>
                    <dd className="mt-1 flex items-baseline justify-between md:block lg:flex">
                      <div className="flex items-baseline text-2xl font-semibold text-primary-600">
                        {item.stat}
                      </div>
                    </dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>
          <div className="border-b border-gray-300 my-3"></div>
        </>
      )}

      <div className="sm:flex justify-between sm:items-center">
        <div className="sm:flex">
          <InputWithCornerHint
            label={t("general.search")}
            id="search"
            name="search"
            className="w-full sm:w-64"
            type="text"
            placeholder={t("general.search")}
            value={searchQuery}
            onChange={onSearchChange}
          />
        </div>
        <div className="flex items-center gap-3 mt-4 md:mt-0 sm:mt-0">
          {!isLoading && (
            <div>
              <button
                onClick={onClickTop}
                type="button"
                className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
              >
                <PlusIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            </div>
          )}

          <ButtonSecondary
            text={t("branch.views.see_fmw_subscription")}
            onClick={OnClickKPI}
            size="large"
          />
        </div>
      </div>
      <div className="border-b border-gray-300 mt-5"></div>
      <div className="flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 mb-3">
          <div className="inline-block min-w-full px-3 py-2 align-middle sm:px-6 lg:px-8">
            {isLoading ? (
              <Fragment>
                <TableLoader columns={12} rows={14} />
              </Fragment>
            ) : filteredFiliales.length === 0 ? (
              <div className="mt-4">
                <Empty
                  onClick={onClickTop}
                  texto={t("branch.views.no_branchs")}
                />
              </div>
            ) : (
              <Fragment>
                <table className="min-w-full divide-y divide-gray-300">
                  <tr>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 w-32"
                    >
                      {t("branch.views.branch")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("branch.views.phone")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("branch.views.country")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("branch.views.cp")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("branch.views.prov_pobl")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("user.views.users")}
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {t("branch.views.fmw_subscription")}
                    </th>
                  </tr>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {filteredFiliales.map((filial) => (
                      <tr key={filial.id}>
                        <td className="px-3 py-3.5 text-left text-sm text-gray-500 w-32">
                          <div
                            onClick={() => navigate(`/branchs/${filial.id}`)}
                            className="cursor-pointer flex items-center"
                          >
                            <AvatarTable
                              image={`${DOMAIN}/uploads/branchs/${filial.logo}`}
                            />
                            <PersonInformationTable
                              name={filial.branch || "-"}
                              email={filial.email || "-"}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-5 text-sm text-gray-500">
                          {filial.phone || "-"}
                        </td>
                        <td className="px-3 py-5 text-sm text-gray-500">
                          {filial.country || "-"}
                        </td>
                        <td className="px-3 py-5 text-sm text-gray-500">
                          {filial.code_zip || "-"}
                        </td>
                        <td className="px-3 py-5 text-sm text-gray-500">
                          {filial.country || "-"}
                          <br />
                          <p className="text-gray-400">
                            {filial.population || "-"}
                          </p>
                        </td>
                        <td className="px-3 py-5 text-sm text-gray-500">
                          {filial.active_users_count}
                        </td>
                        <td className="px-3 py-5 text-sm text-gray-500"></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Fragment>
            )}
          </div>
        </div>
        <div className="mt-4">
          <PaginationUI
            onPageChange={(event) => handlePageChange(event)}
            currentPage={queryMetaData.page}
            pageSize={queryMetaData.perPage}
            totalResults={queryMetaData.total}
            totalPages={queryMetaData.lastPage}
          />
        </div>
      </div>
    </div>
  );
};

export default TableListGyms;
